'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CheckCircle, Loader2, User, Phone, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { sendEmail } from '@/lib/email';

type FormData = {
  fullName: string;
  companyName: string;
  position: string;
  email: string;
  phone: string;
  country: string;
  industry: string;
  heardAbout: string;
  fundingAmount: string;
  fundingPurpose: string;
};

const countries = [
  'United Kingdom', 'United States', 'Canada', 'Australia', 'Germany', 'France',
  'Singapore', 'UAE', 'South Africa', 'Nigeria', 'Kenya', 'India', 'Hong Kong', 'Other',
];

const industries = [
  'Technology & Software', 'Financial Services', 'Real Estate & Property',
  'Manufacturing & Industry', 'Logistics & Supply Chain', 'Healthcare & Life Sciences',
  'Energy & Infrastructure', 'Retail & Consumer', 'Hospitality & Tourism',
  'Media & Entertainment', 'Agriculture & Food', 'Professional Services', 'Other',
];

const fundingRanges = [
  'Under $500K', '$500K – $1M', '$1M – $5M', '$5M – $10M',
  '$10M – $25M', '$25M – $50M', '$50M – $100M', 'Over $100M',
];

const heardAboutOptions = [
  'Personal referral', 'LinkedIn', 'Industry event', 'Press coverage',
  'Professional network', 'Existing member', 'From Bayt', 'Other',
];

export default function RequestAccessForm() {
  const [submitted, setSubmitted] = useState(false);
  const supabase = createClient();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    try {
      const { error } = await supabase.from('access_requests').insert({
        full_name: data.fullName,
        company_name: data.companyName,
        position: data.position,
        email: data.email,
        phone: data.phone,
        country: data.country,
        industry: data.industry,
        heard_about: data.heardAbout,
        funding_amount: data.fundingAmount,
        funding_purpose: data.fundingPurpose,
        status: 'submitted',
      });

      if (error) throw error;

      // Send acknowledgement to applicant
      await sendEmail('access_request_submitted', data.email, data.fullName, {});

      // Notify admin
      await sendEmail('admin_new_access_request', 'admin@operatorcircle.com', 'Administrator', {
        applicant_name: data.fullName,
        company: data.companyName,
        position: data.position,
        email: data.email,
        country: data.country,
        industry: data.industry,
        funding_amount: data.fundingAmount,
      });

      setSubmitted(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to submit application. Please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto card-elevated p-12 text-center fade-in">
        <div className="w-16 h-16 bg-success-bg rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={32} className="text-success" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-3">Application Received</h2>
        <div className="divider-gold mx-auto mb-5" />
        <p className="text-muted-foreground leading-relaxed mb-4">
          Thank you for applying to join Operator Circle. Your application has been received and will be reviewed by our team within <strong className="text-foreground">3–5 business days</strong>.
        </p>
        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
          You will receive a confirmation email shortly. If your application is approved, you will receive your secure portal access credentials by email. All communications are strictly confidential.
        </p>
        <div className="p-4 bg-secondary border border-border rounded-lg text-left">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">What happens next:</strong> Our team will review your application and may contact you for additional information. If approved, a secure account will be created for you and login credentials will be sent to your registered email address.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl mx-auto">
      {/* Section 1: Personal & Professional */}
      <div className="card-elevated p-8 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-gold-light rounded-lg flex items-center justify-center">
            <User size={16} className="text-gold" />
          </div>
          <div>
            <h2 className="font-bold text-foreground text-base">Professional Details</h2>
            <p className="text-muted-foreground text-xs">Your identity and professional background</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <label className="block text-sm font-semibold text-foreground mb-1.5">Full Name <span className="text-danger">*</span></label>
            <input {...register('fullName', { required: 'Full name is required' })} placeholder="e.g. Jonathan Blackwell" className="input-field" />
            {errors.fullName && <p className="text-danger text-xs mt-1">{errors.fullName.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Company Name <span className="text-danger">*</span></label>
            <input {...register('companyName', { required: 'Company name is required' })} placeholder="e.g. Blackwell Capital Group" className="input-field" />
            {errors.companyName && <p className="text-danger text-xs mt-1">{errors.companyName.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Position / Title <span className="text-danger">*</span></label>
            <input {...register('position', { required: 'Position is required' })} placeholder="e.g. Chief Executive Officer" className="input-field" />
            {errors.position && <p className="text-danger text-xs mt-1">{errors.position.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address <span className="text-danger">*</span></label>
            <input type="email" {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' } })} placeholder="j.blackwell@company.com" className="input-field" />
            {errors.email && <p className="text-danger text-xs mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Phone Number <span className="text-danger">*</span></label>
            <input type="tel" {...register('phone', { required: 'Phone number is required' })} placeholder="+44 7700 000000" className="input-field" />
            {errors.phone && <p className="text-danger text-xs mt-1">{errors.phone.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Country <span className="text-danger">*</span></label>
            <select {...register('country', { required: 'Please select your country' })} className="input-field">
              <option value="">Select country</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {errors.country && <p className="text-danger text-xs mt-1">{errors.country.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Industry <span className="text-danger">*</span></label>
            <select {...register('industry', { required: 'Please select your industry' })} className="input-field">
              <option value="">Select industry</option>
              {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
            </select>
            {errors.industry && <p className="text-danger text-xs mt-1">{errors.industry.message}</p>}
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-semibold text-foreground mb-1.5">How did you hear about Operator Circle? <span className="text-danger">*</span></label>
            <select {...register('heardAbout', { required: 'Please select an option' })} className="input-field">
              <option value="">Select option</option>
              {heardAboutOptions.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            {errors.heardAbout && <p className="text-danger text-xs mt-1">{errors.heardAbout.message}</p>}
          </div>
        </div>
      </div>

      {/* Section 2: Capital Requirements */}
      <div className="card-elevated p-8 mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-info-bg rounded-lg flex items-center justify-center">
            <DollarSign size={16} className="text-info" />
          </div>
          <div>
            <h2 className="font-bold text-foreground text-base">Capital Requirements</h2>
            <p className="text-muted-foreground text-xs">Details about your funding requirements</p>
          </div>
        </div>
        <div className="flex flex-col gap-5">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Estimated Funding Amount Required <span className="text-danger">*</span></label>
            <select {...register('fundingAmount', { required: 'Please select a funding range' })} className="input-field">
              <option value="">Select range</option>
              {fundingRanges.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            {errors.fundingAmount && <p className="text-danger text-xs mt-1">{errors.fundingAmount.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Brief Description of Funding Requirement <span className="text-danger">*</span></label>
            <textarea {...register('fundingPurpose', { required: 'Please provide a brief description', minLength: { value: 50, message: 'Please provide at least 50 characters' } })}
              rows={5} placeholder="Briefly describe what the capital is required for..." className="input-field resize-none" />
            {errors.fundingPurpose && <p className="text-danger text-xs mt-1">{errors.fundingPurpose.message}</p>}
          </div>
        </div>
      </div>

      <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-4 text-base">
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 size={18} className="animate-spin" />
            Submitting Application...
          </span>
        ) : 'Submit Access Request'}
      </button>
    </form>
  );
}